/**
 * Shared day/week/month heading and toolbar. Parent props own date navigation, depth, zoom,
 * refresh, and optional tool/action/content slots. Starts the global new-activity flow and command
 * palette without loading calendar records.
 *
 * @module apps/web/src/components/calendar/CalendarChrome
 */

import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Minus, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { openNewActivityFlow } from '@/components/activities/NewActivityFlow';
import { useScheduleStore } from '@/stores/scheduleStore';
import './calendar.css';

/** Shared calendar navigation, density/zoom controls, and action slots; each view owns dates and refresh behavior. */
interface CalendarChromeProps {
  /** Displayed calendar mode and heading label. */
  view: 'Day' | 'Week' | 'Month';
  /** Preformatted current date/range label. */
  period: string;
  /** Navigate to the preceding period. */
  previous: () => void;
  /** Navigate to the following period. */
  next: () => void;
  /** Navigate to the current date. */
  today: () => void;
  /** Whether dimensional calendar styling is enabled. */
  depth: boolean;
  /** Update parent-owned depth styling. */
  setDepth: (value: boolean) => void;
  /** Optional displayed zoom percentage and decrease/increase actions. */
  zoom?: { /** Current zoom value shown by the calendar control. */ value: number; /** Callback requesting one zoom-out step. */ out: () => void; /** Callback requesting one zoom-in step. */ in: () => void };
  /** Optional parent-owned data refresh action. */
  refresh?: () => void;
  /** Disable the refresh control and show its loading state. */
  refreshing?: boolean;
  /** Primary action slot beside activity creation. */
  actions?: ReactNode;
  /** Secondary controls inserted in the calendar toolbar. */
  tools?: ReactNode;
  /** Optional summary content below the toolbar. */
  children?: ReactNode;
}

/**
 * Shared day/week/month heading and toolbar. Parent props own date navigation, depth, zoom,
 * refresh, and optional tool/action/content slots. Starts the global new-activity flow and command
 * palette without loading calendar records.
 */
export function CalendarChrome({
  view,
  period,
  previous,
  next,
  today,
  depth,
  setDepth,
  zoom,
  refresh,
  refreshing,
  actions,
  tools,
  children,
}: CalendarChromeProps) {
  const openAssistant = useScheduleStore(state => state.setCommandPaletteOpen);
  return (
    <>
      <header className="activity-calendar-heading">
        <div className="activity-calendar-title">
          <h1>{view}</h1>
          <p>{period}</p>
        </div>
        <div className="activity-calendar-dates" role="group" aria-label="Calendar dates">
          <Button variant="outline" onClick={today}>
            Today
          </Button>
          <Button variant="outline" size="icon" aria-label="Previous period" onClick={previous}>
            <ChevronLeft />
          </Button>
          <Button variant="outline" size="icon" aria-label="Next period" onClick={next}>
            <ChevronRight />
          </Button>
        </div>
        <nav className="activity-calendar-views" aria-label="Calendar view">
          {(['Day', 'Week', 'Month'] as const).map(label => (
            <NavLink key={label} to={`/${label.toLowerCase()}`}>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="activity-calendar-actions">
          <Button variant="outline" onClick={() => openNewActivityFlow('choose')}>
            <Plus /> Activity
          </Button>
          {actions}
        </div>
      </header>
      <div className="activity-calendar-tools">
        <Button variant="ghost" className="calendar-ask" onClick={() => openAssistant(true)}>
          Ask Fluxability
        </Button>
        <div className="activity-calendar-tool-controls">
          {tools}
          <label className="calendar-depth">
            <input
              type="checkbox"
              checked={depth}
              onChange={event => setDepth(event.target.checked)}
            />{' '}
            Depth
          </label>
          {zoom && (
            <div className="calendar-zoom-control" role="group" aria-label="Timeline zoom">
              <Button variant="ghost" size="icon" aria-label="Zoom calendar out" onClick={zoom.out}>
                <Minus />
              </Button>
              <output aria-live="polite">{zoom.value}%</output>
              <Button variant="ghost" size="icon" aria-label="Zoom calendar in" onClick={zoom.in}>
                <Plus />
              </Button>
            </div>
          )}
          {refresh && (
            <Button
              variant="outline"
              size="icon"
              aria-label="Refresh calendar"
              onClick={refresh}
              disabled={refreshing}
            >
              <RefreshCw className={refreshing ? 'animate-spin' : ''} />
            </Button>
          )}
        </div>
      </div>
      {children && <div className="activity-calendar-summary">{children}</div>}
    </>
  );
}

/**
 * Render the 24-hour timeline caption and legend for activities, commitments, and working hours.
 */
export function CalendarCaption() {
  return (
    <div className="activity-calendar-caption">
      <span>
        Timeline <span className="calendar-muted">24 hours</span>
      </span>
      <div className="activity-calendar-legend">
        <span>Activity</span>
        <span>Commitment</span>
        <span>Working hours</span>
      </div>
    </div>
  );
}
